import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Building2, Shield, Users, Mail, Globe, Wrench } from 'lucide-react';
import { SEO, getBreadcrumbSchema } from '@/hooks/useSEO';
import { useLanguage } from '@/hooks/useLanguage';

export default function AboutPage() {
  const { t } = useTranslation();
  const { lang } = useLanguage();


  const partners = [
    'about.partners.postal',
    'about.partners.surveys',
    'about.partners.authorities',
  ];

  return (
    <div className="container py-12 lg:py-16">
      <SEO
        title={t('about.title')}
        description={t('meta.about')}
        path="/om-oss"
        jsonLd={getBreadcrumbSchema([
          { name: t('common.home'), url: `https://postnummerservice.se/${lang}` },
          { name: t('about.title'), url: `https://postnummerservice.se/${lang}/om-oss` },
        ])}
      />
      <div className="grid gap-12 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-12">
          {/* Hero Section */}
          <section>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('about.title')}
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {t('about.subtitle')}
            </p>
          </section>

          {/* About Section */}
          <section>
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-2">{t('about.company.title')}</h2>
              </div>
            </div>
            <div className="space-y-4 text-muted-foreground leading-relaxed pl-16">
              <p>{t('about.company.p1')}</p>
              <p>{t('about.company.p2')}</p>
              <p>{t('about.company.p3')}</p>
            </div>
          </section>

          {/* Quality Section */}
          <section>
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-2">{t('about.quality.title')}</h2>
              </div>
            </div>
            <div className="space-y-4 text-muted-foreground leading-relaxed pl-16">
              <p>{t('about.quality.p1')}</p>
              <p>{t('about.quality.p2')}</p>
              <div className="bg-muted/50 rounded-lg p-4 mt-4">
                <p className="font-medium text-foreground mb-2">{t('about.quality.partnersTitle')}</p>
                <ul className="space-y-1">
                  {partners.map((key, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-secondary" />
                      {t(key)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>



        </div>

        {/* Sidebar - Contact Info */}
        <aside className="lg:col-span-1">
          <div className="sticky top-24">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-secondary" />
                  </div>
                  <h3 className="font-semibold">{t('about.contact.title')}</h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="font-medium text-foreground">Postnummerservice Norden AB</p>
                    <p className="text-sm text-muted-foreground">Biblioteksgatan 29</p>
                    <p className="text-sm text-muted-foreground">114 35 Stockholm</p>
                  </div>

                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <a href="mailto:info@postnummerservice.se" className="hover:text-secondary transition-colors">
                      info@postnummerservice.se
                    </a>
                  </div>

                  <div className="flex items-center gap-3 text-sm">
                    <Wrench className="w-4 h-4 text-muted-foreground" />
                    <a href="mailto:support@postnummerservice.se" className="hover:text-secondary transition-colors">
                      support@postnummerservice.se
                    </a>
                  </div>

                  <div className="flex items-center gap-3 text-sm">
                    <Globe className="w-4 h-4 text-muted-foreground" />
                    <a href="https://postnummerservice.se" className="hover:text-secondary transition-colors">
                      postnummerservice.se
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </aside>
      </div>
    </div>
  );
}
